import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import ReceivablesTable, { RECEIVABLES_EXPORT_COLUMNS } from "./ReceivablesTable";
import { exportTable } from "../../../utils/exportTable";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { btn } from "../../../components/Button/buttonStyles";

const ReceivablesPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Account managers view receivables only - but they own client invoicing,
  // so the "add to new invoices" actions stay available to them.
  const readOnly = isReadOnly(user, MODULES.RECEIVABLES);
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  /* Fetch Receivables */
  const fetchReceivables = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get(`/project-finances/receivables`, {
        withCredentials: true,
      });
      setReceivables(response.data.data || []);
    } catch (err) {
      console.error("Error fetching receivables:", err);
      setError("Failed to fetch receivables");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceivables();
  }, []);

  /* Selection handlers */
  const handleToggle = (rowKey) => {
    setSelectedIds((prev) =>
      prev.includes(rowKey) ? prev.filter((k) => k !== rowKey) : [...prev, rowKey]
    );
  };

  const handleToggleAll = (data) => {
    const allKeys = data.map((r) => `${r.type}-${r.id}`);
    const allSelected = allKeys.every((k) => selectedIds.includes(k));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((k) => !allKeys.includes(k)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allKeys])]);
    }
  };

  /* Action handlers */
  const handleEdit = (row) => {
    if (row.type === "flat_rate") {
      navigate(`/project/${row.project_id}/edit-flat-rate-receivable/${row.id}`);
    } else {
      navigate(`/project/${row.project_id}/edit-unit-based-receivable/${row.id}`);
    }
  };

  const handleDelete = async (row) => {
    if (!await confirmDialog(`Delete receivable ${row.receivable_code || ""}?`)) return;
    const endpoint =
      row.type === "flat_rate"
        ? `/project-finances/flat-rate-receivables/${row.id}`
        : `/project-finances/unit-based-receivables/${row.id}`;
    try {
      const { data } = await api.delete(endpoint, { withCredentials: true });
      if (data.success) {
        setSelectedIds((prev) => prev.filter((k) => k !== `${row.type}-${row.id}`));
        fetchReceivables();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleClone = async (row) => {
    const endpoint =
      row.type === "flat_rate"
        ? `/project-finances/flat-rate-receivables`
        : `/project-finances/unit-based-receivables`;

    const {
      id, createdAt, updatedAt, type,
      project, service, languagePair, currency, unit, file,
      ...payload
    } = row;

    try {
      const { data } = await api.post(endpoint, { ...payload, project_id: Number(row.project_id) }, { withCredentials: true });
      if (data.success) fetchReceivables();
    } catch (err) {
      console.error("Clone failed:", err);
    }
  };

  const handleAddSelectionToInvoice = async () => {
    if (selectedIds.length === 0) return;
    const receivables = selectedIds.map((key) => {
      const [type, id] = key.split("-");
      return { type, id: Number(id) };
    });
    try {
      const { data } = await api.post(
        "/project-finances/client-invoices",
        { receivables },
        { withCredentials: true }
      );
      if (data.success) navigate(`/client/invoice/${data.data.id}`);
    } catch (err) {
      console.error("Create invoice failed:", err);
      toast.error(err.response?.data?.message || "Failed to create invoice.");
    }
  };

  const handleAddFilterToInvoice = () => {
    console.log("All filtered receivables for invoice:", receivables.map((r) => `${r.type}-${r.id}`));
  };

  const handleExportToExcel = () => {
    exportTable("client-receivables", RECEIVABLES_EXPORT_COLUMNS, receivables);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Receivables</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={handleAddSelectionToInvoice}
            disabled={selectedIds.length === 0}
            className={btn("primary", "sm")}
          >
            Add (selection) to new invoices
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={handleAddFilterToInvoice}
            className={btn("primary", "sm")}
          >
            Add (filter results) to new invoices
          </button>
          <button
            onClick={handleExportToExcel}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{receivables.length}</span> items.
          </p>

          <ReceivablesTable
            data={receivables}
            readOnly={readOnly}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClone={handleClone}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            onToggleAll={handleToggleAll}
          />
        </>
      )}
    </div>
  );
};

export default ReceivablesPage;